// Global Instructions Rule Applied!
// Shapes of the Investor form's reads and write (backend module `investors`):
//   GET   /investors/:id              — view__investor_detail (also what
//                                       POST / PATCH /investors return)
//   GET   /investors/:id/commitments  — view__investor_commitments
//   GET   /investors/form-options     — view__investor_form_options
// Ids are plain z.string() (legacy Dataverse ids preclude .uuid()).
import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

export const InvestorDetailSchema = z
  .object({
    investors__id: z.string(),
    investors__name: z.string(),
    investors__legal_name: z.string().nullable(),
    investors__investor_number: z.string().nullable(),
    accounts__id: z.string().nullable(),
    accounts__name: z.string().nullable(),
    investor_classifications__id: z.number().nullable(),
    investor_classifications__label: z.string().nullable(),
    legal_entity_types__id: z.number().nullable(),
    legal_entity_types__label: z.string().nullable(),
    sbic_investor_types__id: z.number().nullable(),
    sbic_investor_types__label: z.string().nullable(),
    // cfg.notice_templates supplements the investor's capital call notices
    // carry by default (a commitment can use its own setting)
    prog__notice_supplement_template_ids: z.array(z.number())
  })
  .strict()

export const InvestorDetailFields = makeFieldKeys(InvestorDetailSchema)

// One standalone commitment ('fund') or one consolidated commitment
// ('consolidated') of the investor. The pockets of a consolidated commitment
// are not listed.
export const InvestorCommitmentRowSchema = z
  .object({
    prog__kind: z.enum(['fund', 'consolidated']),
    // fund_commitments.id | fund_group_commitments.id, by kind
    prog__commitment_id: z.string(),
    funds__id: z.string().nullable(),
    funds__name: z.string().nullable(),
    fund_groups__id: z.string().nullable(),
    fund_groups__name: z.string().nullable(),
    prog__amount: z.number().nullable(),
    prog__amount_fmt: z.string().nullable(),
    fund_commitment_statuses__id: z.number().nullable(),
    fund_commitment_statuses__label: z.string().nullable(),
    // 'YYYY-MM-DD'
    prog__date_committed: z.string().nullable(),
    prog__date_closed: z.string().nullable(),
    prog__is_gp_commitment: z.boolean(),
    // true = the commitment uses its own notice documents, not the investor's
    prog__notice_supplements_overridden: z.boolean(),
    // the documents the commitment effectively carries
    prog__notice_supplement_labels: z.array(z.string())
  })
  .strict()

const NumberOptionSchema = z.object({ value: z.number(), label: z.string() }).strict()

export const InvestorFormOptionsSchema = z
  .object({
    legal_entity_types: z.array(NumberOptionSchema),
    notice_supplements: z.array(NumberOptionSchema)
  })
  .strict()

export type InvestorDetail = z.infer<typeof InvestorDetailSchema>
export type InvestorCommitmentRow = z.infer<typeof InvestorCommitmentRowSchema>
export type InvestorFormOptions = z.infer<typeof InvestorFormOptionsSchema>

export const validateInvestorDetail = (data: unknown): InvestorDetail =>
  validateWithZodSchema(InvestorDetailSchema, data, 'investor detail')

export const validateInvestorCommitmentRows = (data: unknown): InvestorCommitmentRow[] =>
  validateWithZodSchema(InvestorCommitmentRowSchema, data, 'investor commitment row', { isArray: true })

export const validateInvestorFormOptions = (data: unknown): InvestorFormOptions =>
  validateWithZodSchema(InvestorFormOptionsSchema, data, 'investor form options')
